import React from 'react';
import { ButtonV2, Flex, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { useUnlockController } from '../shared/useUnlockController';
import { StyledFormStack, StyledTitleStack, StyledHeaderStack, StyledInputsStack, StyledContent, styles } from './UnlockForm.styles';
import { StyledInputWrapper } from '../shared/UnlockInputWrapper.styles';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body2ToSubHeadingOverride, hed6ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useTranslation } from '@shared/core';
import { useDeferredInputFocus } from '../useDeferredInputFocus';
import { useUnlockDialogInputStyles, useUnlockDialogLinkStyles } from '../useUnlockDialogButtonStyles';
import { UnlockSubmitButton } from '../shared/UnlockSubmitButton';

type UnlockFormProps = Readonly<{
  eventId: string;
  onUnlockSuccess: () => void;
  onShowAskHost: () => void;
}>;

export const UnlockForm: React.FC<UnlockFormProps> = ({ eventId, onUnlockSuccess, onShowAskHost }) => {
  const { formik, loading, inputError, onSignInHandler } = useUnlockController({ eventId, onUnlockSuccess });
  const { tq } = useTranslation('guestUnlock');
  const inputRef = useDeferredInputFocus();
  const inputStyles = useUnlockDialogInputStyles();
  const linkStyles = useUnlockDialogLinkStyles();

  return (
    <StyledContent
      __css={styles.content}
      data-testid="unlock-form"
    >
      <StyledHeaderStack>
        <StyledTitleStack>
          <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
            <TextV2 typographyVariant="hed6">{tq`Private Event`}</TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body2ToSubHeadingOverride}>
            <TextV2 typographyVariant="body2">{tq`Use the password shared by your host to see event details. `}</TextV2>
          </GuestSiteTypographyOverride>
          <Flex
            alignItems="center"
            gap={3}
          >
            <TextV2 typographyVariant="label3">{tq`Need it?`}</TextV2>
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="label3"
              width="fit-content"
              height="fit-content"
              padding={0}
              borderBottom="1px solid"
              borderBottomColor="currentColor"
              borderBottomLeftRadius="0px"
              borderBottomRightRadius="0px"
              onClick={onShowAskHost}
              {...linkStyles}
            >
              {tq`Ask the Host`}
            </ButtonV2>
          </Flex>
        </StyledTitleStack>
      </StyledHeaderStack>
      <StyledFormStack
        spacing={6}
        __css={styles.formStack}
      >
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsStack>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={tq`Event Password`}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  ref={inputRef}
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={tq`eg. xy4686`}
                  isInvalid={(inputError && formik.touched.password) || undefined}
                  isDisabled={loading}
                  {...formik.getFieldProps('password')}
                />
              </FormControl>
            </StyledInputWrapper>
          </StyledInputsStack>
          <UnlockSubmitButton
            shape="rounded"
            width={'100%'}
            variant="outline"
            type="submit"
            loading={loading}
          >
            {tq`View Event`}
          </UnlockSubmitButton>

          <Flex
            justifyContent="center"
            marginTop={6}
          >
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="button2"
              onClick={onSignInHandler}
              width="fit-content"
              height="fit-content"
              padding={0}
              borderBottom="1px solid"
              borderBottomColor="currentColor"
              borderBottomLeftRadius="0px"
              borderBottomRightRadius="0px"
              {...linkStyles}
            >
              {tq`Sign In`}
            </ButtonV2>
          </Flex>
        </form>
      </StyledFormStack>
    </StyledContent>
  );
};
